import React, { useEffect } from 'react';
import t from 'react-translate';
import { useSelector, useDispatch } from 'react-redux';
import NvIcon from 'shared/components/nv-icon';
import { voteNovoAiAnswer, setVoteLoading, setVoteState, showVoteFeedback, VoteTypes } from 'redux/actions/help-options';
import { getCurrentCourse } from 'redux/selectors/course';
import { getVoteState } from 'redux/selectors/help-options';
import { RootState } from 'redux/schemas';
import { SaveStatus } from 'redux/schemas/app/mentoring-programs';
import SaveToast from 'athena/components/mentoring-program/program-configuration/save-toast';
import { helpfulnessStyles } from '../styles';

interface HelpfulnessRatingProps {
  questionId?: number;
}

const HelpfulnessRating: React.FC<HelpfulnessRatingProps> = ({
  questionId,
}) => {
  const dispatch = useDispatch();
  const currentCourse = useSelector(getCurrentCourse);
  const voteState = useSelector((state: RootState) => {
    if (!questionId) {
      return {
        upvoted: false,
        downvoted: false,
        isLoading: false,
        showFeedback: false,
      };
    }
    return getVoteState(state, questionId);
  });

  const catalogId = currentCourse?.catalogId;

  // Auto-hide feedback after 3 seconds
  useEffect(() => {
    if (voteState.showFeedback && questionId) {
      const timeout = setTimeout(() => {
        dispatch(showVoteFeedback(questionId, false));
      }, 3000);

      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [voteState.showFeedback, questionId, dispatch]);

  const handleVote = async (voteType: VoteTypes) => {
    if (!questionId || !catalogId || voteState.isLoading) return;
    // If already voted, do nothing
    if (voteState.upvoted || voteState.downvoted) return;

    try {
      await dispatch(
        voteNovoAiAnswer({
          catalogId,
          qnaId: questionId,
          vote: voteType,
        }),
      );
      dispatch(setVoteState(questionId, voteType, true));
      dispatch(showVoteFeedback(questionId, true));
    } catch (error) {
      dispatch(setVoteLoading(questionId, false));
    }
  };

  if (!questionId) {
    return null;
  }

  return (
    <div css={helpfulnessStyles}>
      {voteState.showFeedback && (
        <div className='save-toast'>
          <SaveToast status={SaveStatus.THANK_YOU} />
        </div>
      )}
      <div className='helpfulness-container'>
        <span className='helpfulness-text'>
          {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.IS_HELPFUL()}
        </span>
        <div className='vote-buttons'>
          <button
            type='button'
            className={`vote-button${voteState.isLoading || voteState.downvoted ? ' disabled' : ''}`}
            onClick={() => handleVote(VoteTypes.UPVOTE)}
            disabled={voteState.isLoading || voteState.upvoted || voteState.downvoted}
            aria-label={t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.HELPFUL()}
          >
            <NvIcon
              icon={voteState.upvoted ? 'like-filled' : 'like'}
              size='small'
              className={`vote-icon${voteState.upvoted ? ' voted' : ''}`}
            />
          </button>
          <button
            type='button'
            className={`vote-button down${
              voteState.isLoading || voteState.upvoted ? ' disabled' : ''
            }`}
            onClick={() => handleVote(VoteTypes.DOWNVOTE)}
            disabled={voteState.isLoading || voteState.upvoted || voteState.downvoted}
            aria-label={t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.NOT_HELPFUL()}
          >
            <NvIcon
              icon={voteState.downvoted ? 'like-filled' : 'like'}
              size='small'
              className={`vote-icon${voteState.downvoted ? ' voted' : ''}`}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default HelpfulnessRating;
